import type {
  ColetorDistribuicao,
  DistribuicaoColetor,
  ParteDaDistribuicao,
} from "@/modules/financeiro-configuracoes";
import type { NoArvore } from "./distribuicao-arvore";
import {
  DISTRIBUICAO_CARGA_INICIAL,
  DISTRIBUICAO_FACILIDADES_NOTA,
  DISTRIBUICAO_TAG_FIXO,
  DISTRIBUICAO_TAG_INATIVA,
  PARTE_ROTULOS,
} from "./distribuicoes.constants";
import type { AcaoPendente } from "./types-distribuicoes";

const PERCENTUAL_FORMATTER = new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 2 });

const DATA_FORMATTER = new Intl.DateTimeFormat("pt-BR", {
  timeZone: "America/Sao_Paulo",
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});
const HORA_FORMATTER = new Intl.DateTimeFormat("pt-BR", {
  timeZone: "America/Sao_Paulo",
  hour: "2-digit",
  minute: "2-digit",
});

export const formatPercentual = (valor: number): string => `${PERCENTUAL_FORMATTER.format(valor)}%`;

export function formatUltimaAlteracao(instante: string, por: string): string {
  const data = new Date(instante);
  if (Number.isNaN(data.getTime())) return por;
  const dia = DATA_FORMATTER.format(data);
  return por === DISTRIBUICAO_CARGA_INICIAL
    ? `${DISTRIBUICAO_CARGA_INICIAL} em ${dia}`
    : `Alterada em ${dia} às ${HORA_FORMATTER.format(data)} por ${por}`;
}

const SEM_ACOES = { editar: null, situacao: null } as const;

function noColetor(prefixo: string, coletor: ColetorDistribuicao): NoArvore {
  return {
    id: `${prefixo}:${coletor.id}`,
    rotulo: `${coletor.codigo} · ${coletor.nome}`,
    percentual: formatPercentual(coletor.percentual),
    ultimaAlteracao: "",
    tags: [],
    inativo: false,
    ...SEM_ACOES,
    filhos: coletor.filhos.map((filho) => noColetor(prefixo, filho)),
  };
}

function noParte(prefixo: string, parte: ParteDaDistribuicao): NoArvore {
  const id = `${prefixo}:${parte.parte}`;
  const facilidades = parte.parte === "FACILIDADES";
  return {
    id,
    rotulo: PARTE_ROTULOS[parte.parte],
    percentual: formatPercentual(parte.total),
    ultimaAlteracao: facilidades ? DISTRIBUICAO_FACILIDADES_NOTA : "",
    fixo: facilidades,
    tags: facilidades ? [DISTRIBUICAO_TAG_FIXO] : [],
    inativo: false,
    ...SEM_ACOES,
    filhos: parte.coletores.map((coletor) => noColetor(id, coletor)),
  };
}

export function montarNoDistribuicao(
  distribuicao: DistribuicaoColetor,
  abrir: ((acao: AcaoPendente) => void) | null,
): NoArvore {
  const tipo = distribuicao.ativo ? "inativar" : "ativar";

  return {
    id: distribuicao.id,
    rotulo: distribuicao.nome,
    percentual: "",
    ultimaAlteracao: formatUltimaAlteracao(distribuicao.alteradaEm, distribuicao.alteradaPor),
    tags: distribuicao.ativo ? [] : [DISTRIBUICAO_TAG_INATIVA],
    inativo: !distribuicao.ativo,
    editar: abrir
      ? {
          label: `Editar distribuição ${distribuicao.nome}`,
          tooltip: "Editar distribuição",
          testId: `distribuicao-editar-${distribuicao.id}`,
          executar: () => abrir({ tipo: "editar", distribuicao }),
        }
      : null,
    situacao: abrir
      ? {
          tipo,
          label: `${tipo === "inativar" ? "Inativar" : "Ativar"} distribuição ${distribuicao.nome}`,
          tooltip: tipo === "inativar" ? "Inativar distribuição" : "Ativar distribuição",
          testId: `distribuicao-situacao-${distribuicao.id}`,
          executar: () => abrir({ tipo, distribuicao }),
        }
      : null,
    filhos: distribuicao.partes.map((parte) => noParte(distribuicao.id, parte)),
  };
}
